// ONDEMAND-1: the "opened" trigger. Opening a recording's detail screen is one of the three
// things (opened / searched / asked about) that makes a deferred recording worth transcribing,
// and the first open of a transcribed-but-unsummarized one (an import) is what starts its
// summary. This hook fires `ensure` ONCE per recording id per visit — the server is idempotent
// anyway, but re-renders and the recording's own refetches must not re-fire it.
//
// EXCEPTION: landing on the detail straight after pressing Stop is not "opening" (see
// lib/justRecorded.ts) — no auto-ensure then; the caller offers "Transcribe now" instead.
import { useEffect, useRef, useState } from 'react';
import { isDeferred, type Recording, type TranscriptionStatus } from '@scribe/core/types';
import { takeJustRecorded } from '../lib/justRecorded';
import { useEnsureTranscription } from '../lib/ondemandQueries';

export interface OpenTranscription {
  /** This visit is the post-capture landing — no auto-ensure. */
  justRecorded: boolean;
  /** The ensure result for THIS recording id, if one has come back. */
  result: TranscriptionStatus | undefined;
  pending: boolean;
  error: Error | null;
  /** The explicit "Transcribe now" click. */
  transcribeNow: () => void;
}

export function useOpenTranscription(
  id: string,
  recording: Recording | undefined,
  enabled: boolean,
): OpenTranscription {
  const ensure = useEnsureTranscription();
  const { mutate } = ensure;
  // Taken once per id, like RecordingDetail's carried captions: the stash clears on read and an
  // id change (App reuses this screen across recordings) re-takes it for the new id.
  const [visit, setVisit] = useState(() => ({ id, justRecorded: takeJustRecorded(id) }));
  if (visit.id !== id) setVisit({ id, justRecorded: takeJustRecorded(id) });
  const justRecorded = visit.id === id && visit.justRecorded;

  const firedFor = useRef<string | null>(null);
  // Only a loaded row for THIS id counts (while the next id's query is in flight `recording`
  // can still be the previous one). Deferred -> start transcription; completed -> start a
  // missing summary. Anything mid-pipeline/failed is left alone.
  const eligible =
    enabled &&
    !justRecorded &&
    recording?.id === id &&
    (isDeferred(recording) || recording.status === 'completed');

  useEffect(() => {
    if (!eligible || firedFor.current === id) return;
    firedFor.current = id;
    mutate({ recordingId: id, trigger: 'opened' });
  }, [eligible, id, mutate]);

  const forThisId = ensure.variables?.recordingId === id;
  return {
    justRecorded,
    result: forThisId ? ensure.data : undefined,
    pending: forThisId && ensure.isPending,
    error: forThisId ? (ensure.error as Error | null) : null,
    transcribeNow: () => {
      firedFor.current = id;
      mutate({ recordingId: id, trigger: 'opened' });
    },
  };
}
